// src/bootstrap/http/errors.js

import axios from 'axios'
import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { EventBus } from '../../eventBus.js'

const RECORD_NOT_EDITABLE = 'RECORD_NOT_EDITABLE'

// The server cannot translate, and every caller shows response.data in a toast, so the machine
// readable body is swapped for a sentence in the user's language before it reaches them.
function describe (body) {
  const translator = new Vue()
  const text = body.statusTypeId === 5
    ? translator.$ml.get('This record was deleted while you were editing it')
    : translator.$ml.get('This record was closed while you were editing it')
  const notSaved = translator.$ml.get('Your changes were not saved. See the History tab for who changed it.')

  return `${text} (${body.serialNo || body.recordId}). ${notSaved}`
}

// Returns the message to show, or null when this is not a row-status rejection.
function describeIfNotEditable (status, body) {
  if (status !== 409 || !body || body.code !== RECORD_NOT_EDITABLE) {
    return null
  }

  EventBus.$emit('recordNotEditable', body)

  return describe(body)
}

// ---------- AXIOS ----------
axios.interceptors.response.use(
  response => response,
  error => {
    const response = error.response

    if (!response || response.status >= 500) {
      Sentry.captureException(error)
    }
    else {
      const message = describeIfNotEditable(response.status, response.data)
      if (message) {
        response.data = message
      }
    }

    if (response && response.status === 403) {
      EventBus.$emit('accessDenied')
    }

    return Promise.reject(error)
  }
)

// ---------- VUE-RESOURCE ----------
Vue.http.interceptors.push((request, next) => {
  next(response => {
    // vue-resource uses response.ok
    if (!response.ok && response.status >= 500) {
      const error = new Error(`HTTP ${response.status}`)
      error.request = request
      error.response = response

      Sentry.captureException(error)
    }
    else if (!response.ok) {
      const message = describeIfNotEditable(response.status, response.body || response.data)
      if (message) {
        response.body = message
        response.data = message
      }
    }

    // A 403 means the API knows who the caller is and is refusing anyway; 401 means it does not, and
    // auth.js already turns that into a redirect to login. Saying so beats a screen that keeps its
    // chrome and shows nothing, which reads as broken.
    if (response.status === 403) {
      EventBus.$emit('accessDenied')
    }

    return response
  })
})
